import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: { queries: { retry: false, staleTime: 30_000, refetchOnWindowFocus: false } }
  });
}

export const queryKeys = {
  workspaces: () => ['workspaces'] as const,
  me: () => ['auth', 'me'] as const,
  onboarding: (workspaceId: string) => ['workspaces', workspaceId, 'onboarding'] as const,
  connectedAgents: () => ['account', 'agents'] as const,
  reports: (workspaceId: string) => ['workspaces', workspaceId, 'reports'] as const,
  report: (workspaceId: string, reportId: string) => ['workspaces', workspaceId, 'reports', reportId] as const,
  reportData: (workspaceId: string, reportId: string) =>
    ['workspaces', workspaceId, 'reports', reportId, 'data'] as const,
  reportActivity: (workspaceId: string, reportId: string) =>
    ['workspaces', workspaceId, 'reports', reportId, 'activity'] as const,
  reportRevision: (workspaceId: string, reportId: string, revision: number) =>
    ['workspaces', workspaceId, 'reports', reportId, 'revisions', revision] as const,
  brand: (workspaceId: string) => ['workspaces', workspaceId, 'brand'] as const,
  theme: (workspaceId: string) => ['workspaces', workspaceId, 'theme'] as const,
  tokens: (workspaceId: string) => ['workspaces', workspaceId, 'tokens'] as const,
  publicReport: (linkId: string) => ['public', 'reports', linkId] as const,
  exportPayload: (exportId: string) => ['exports', exportId, 'payload'] as const,
  cardPayload: (exportId: string) => ['exports', exportId, 'card'] as const
};

export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces(),
    queryFn: () => api.listWorkspaces().then((r) => r.workspaces)
  });
}

export function useReports(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.reports(workspaceId),
    queryFn: ({ signal }) => api.listReports(signal, workspaceId)
  });
}

export function useCreateWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createWorkspace(name),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.workspaces() })
  });
}
